import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import {useMutation, useQuery} from '@apollo/client'
import { Button, Checkbox, Modal } from 'antd'
import CardTemplate, { CardFooter, CardInner, } from 'templates/CardTemplate/CardTemplate'

import ACCEPT_PRIVACY_AGREEMENT from 'context/AppPersonContext/_gql/ACCEPT_PRIVACY_AGREEMENT'
import CHECK_TOKEN from 'context/AppPersonContext/_gql/CHECK_TOKEN'

import { urlRootPath } from 'helpers/urlPath'
import { SPORT_URL_PART } from 'pages/Sport/_constants/SPORT_URLS'
import { PRIVACY_AGREEMENT, REGISTER, TERMS_OF_USE } from 'pages/Registration/URL'
import { PRIVACY_POLICY_URL_PART } from 'constants/URL'

import { normalizeUrlPath } from 'helpers/normalizeUrlPath'
import _Icon_DocumentPrivacy from "../Icon/_icons/_Icon_DocumentPrivacy";
import {GET_ACTIVE_PRIVACY_DOCUMENT} from "../../gql/queries/GET_ACTIVE_PRIVACY_DOCUMENT";

export const ModalPrivacyAgreement = ({ hasAgreementWithActiveDocument }) => {
  const { t } = useTranslation()
  const { data , loading: metaLoading } = useQuery(GET_ACTIVE_PRIVACY_DOCUMENT);
  const initialOpen = hasAgreementWithActiveDocument === undefined ? false : !hasAgreementWithActiveDocument;
  const [isModalOpen, setIsModalOpen] = useState(initialOpen);
  const [agreement, setAgreement] = useState(false);
  const [acceptPrivacyAgreement, { loading }] = useMutation(
    ACCEPT_PRIVACY_AGREEMENT,
    {
      refetchQueries: [CHECK_TOKEN],
      awaitRefetchQueries: true,
    },
  );

  const location = useLocation();
  const currentPath = normalizeUrlPath(location.pathname);
  const hideOnPages = [
      urlRootPath(SPORT_URL_PART, PRIVACY_POLICY_URL_PART),
      urlRootPath(SPORT_URL_PART, REGISTER, PRIVACY_AGREEMENT),
      urlRootPath(SPORT_URL_PART, REGISTER, TERMS_OF_USE),
      '/404'
  ].map(normalizeUrlPath);


  const handleAgreementChange = (e) => {
    const checked = e && e.target ? e.target.checked : !!e;
    setAgreement(checked);
  };

  const handleAccept = async () => {
    await acceptPrivacyAgreement();
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (hasAgreementWithActiveDocument === undefined) {
      setIsModalOpen(false);
      setAgreement(false);
      return;
    }

    setIsModalOpen(!hasAgreementWithActiveDocument);
    setAgreement(false);
  }, [hasAgreementWithActiveDocument]);

  const shouldHide = hideOnPages.some(path => currentPath.startsWith(path));

  if (shouldHide) {
    return null;
  }

  return (
    <Modal
      title={""}
      footer={""}
      closable={false}
      centered={true}
      open={isModalOpen}
      onClickClose={() => setIsModalOpen(false)}
      css={`
        width: auto !important;
      `}
    >
      <CardTemplate
        css={`
          max-width: 600px;
          display: flex;
          flex-direction: column;
          row-gap: 14px;

          @media (max-width: 768px) {
            width: auto;
          }
        `}
      >
        <CardInner
          css={`
            padding: 14px 14px 0 20px;
            display: flex;
            align-items: center;
            gap: 8px;
            font-size: 15px;

            @media (max-width: 768px) {
              flex-direction: column;
              text-align: center;
            }
          `}
        >
          <div>
            <_Icon_DocumentPrivacy size={72}></_Icon_DocumentPrivacy>
          </div>

          <p
            css={`
              margin: 0;
              font-size: 15px;
            `}
          >
            <span
              css={`
                font-weight: 700;
              `}
            >
              {t('Updated personal data processing rules')}
            </span>
            <br />
            {t('To continue using the Sport Vokrug. platform, you must confirm your consent to personal data processing.')}
          </p>
        </CardInner>

        { Boolean(data?.activePrivacyDocument?.version) && (
          <CardFooter
            css={`
            padding: 14px 14px 14px 20px;
            display: flex;
            flex-direction: column;
            row-gap: 14px;
            justify-content: space-between;
            border-top: 1px solid #f2f2f2;
            margin-top: 0;
          `}
          >
            <Checkbox
              checked={agreement}
              onChange={handleAgreementChange}
              css={`
              text-align: center;
              font-weight: 400;

              .ant-checkbox + span {
                padding: 0 0 0 10px;

                @media (max-width: 768px) {
                  font-size: 13px;
                }
              }
            `}
            >
              {t('I consent to personal data processing in accordance with')}{" "}
              <a
                className="lightBlue"
                href={urlRootPath(SPORT_URL_PART, REGISTER, PRIVACY_AGREEMENT, encodeURIComponent(data?.activePrivacyDocument?.version))}
                rel="noopener noreferrer"
                css={`
                  color: #05b0f0;
  `             }
                target="_blank"
              >
                {t('Personal data processing agreement')}
              </a>
            </Checkbox>

            <Button
              type="primary"
              disabled={!agreement}
              onClick={handleAccept}
              css={`
              max-width: 220px;
              align-self: center;
            `}
            >
              {t('Accept and continue')}
            </Button>
          </CardFooter>
        ) }
      </CardTemplate>
    </Modal>
  );
};
